import { FetchResult } from '@apollo/client';
import { Button, Elevation, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { convertFromHTML, convertToHTML } from 'draft-convert';
import { EditorState } from 'draft-js';
import { Formik } from 'formik';
import { useSubmit } from 'formik-apollo';
import React from 'react';
import * as Yup from 'yup';
import { ContentCard, ContentCardFooter, ContentCardHeader, ContentCardScroll } from '../../../components/ContentCard';
import FormGroup from '../../../components/FormGroup';
import RichTextInput, { decorator, entityToHTML, htmlToEntity } from '../../../components/RichTextInput';
import Text from '../../../components/Text';
import { CancelEventInput, CancelEventMutation, useCancelEventMutation } from '../../../generated/graphql';
import { getLaravelValidationErrors, showFormErrorMessage } from '../../../helpers/graphql';

const validationSchema = Yup.object({});

type EventCancelFormProps = {
  onCancel?: () => void;
  onSuccess?: () => void;
  initialValues: CancelEventInput;
};

type EventCancelFormValues = Omit<CancelEventInput, 'reason'> & {
  reason: EditorState;
};

const EventCancelForm = ({ onCancel, onSuccess, initialValues }: EventCancelFormProps) => {
  const [cancelEvent] = useCancelEventMutation();
  const handleSubmit = useSubmit<EventCancelFormValues, FetchResult<CancelEventMutation>>({
    mutate: (values) =>
      cancelEvent({
        variables: {
          input: {
            ...values,
            reason: convertToHTML({ entityToHTML })(values.reason.getCurrentContent()),
          },
        },
      }),
    onCompleted: () => onSuccess?.(),
    onError: showFormErrorMessage,
    getErrors: getLaravelValidationErrors,
  });

  return (
    <Formik
      initialValues={{
        ...initialValues,
        reason: EditorState.createWithContent(convertFromHTML({ htmlToEntity })(initialValues.reason), decorator),
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, submitForm }) => (
        <ContentCard elevation={Elevation.FOUR}>
          <ContentCardHeader
            leftElement={<Text large>Veranstaltung absagen</Text>}
            rightElement={<Button onClick={onCancel} icon={IconNames.CROSS} minimal />}
          />

          <ContentCardScroll>
            <FormGroup
              label="Grund"
              name="reason"
              labelInfo="(erforderlich)"
              helperText="Wird per Email an Teilnehmer kommuniziert"
            >
              <RichTextInput name="reason" />
            </FormGroup>
          </ContentCardScroll>

          <ContentCardFooter
            rightElement={
              <>
                <Button text="Abbrechen" onClick={onCancel} className="ml-2" />
                <Button
                  text="Absagen"
                  loading={isSubmitting}
                  intent={Intent.DANGER}
                  onClick={submitForm}
                  className="ml-2"
                />
              </>
            }
          />
        </ContentCard>
      )}
    </Formik>
  );
};

export default EventCancelForm;
